import { RouteError } from '@/components/RouteError';
import { RequireAuth } from '@/features/auth/RequireAuth';
import { env } from '@/lib/env';
import { NotFound } from '@/pages/NotFound';
import { PublicDossierPage } from '@/pages/PublicDossierPage';
import { ServiceEntryDetail } from '@/pages/ServiceEntryDetail';
import { ServiceLog } from '@/pages/ServiceLog';
import { SignIn } from '@/pages/SignIn';
import { VehicleDetail } from '@/pages/VehicleDetail';
import { VehicleList } from '@/pages/VehicleList';
import { VerifyPage } from '@/pages/VerifyPage';
import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AppLayout } from './AppLayout';

// Route-level code splitting: these pages are off the critical
// "open app -> vehicle list -> vehicle detail/dossier" path, so they
// ship as separate chunks instead of inflating the initial bundle.
// Named exports need the `.then` shim since React.lazy only takes a
// default export. All chunks are precached by the Workbox globPatterns
// in vite.config.ts, so this stays offline-safe once visited or after
// the first online precache pass.
const VehicleGenesisPage = lazy(() =>
  import('@/pages/VehicleGenesisPage').then((m) => ({ default: m.VehicleGenesisPage })),
);
const ProjectsPage = lazy(() =>
  import('@/pages/ProjectsPage').then((m) => ({ default: m.ProjectsPage })),
);
const InsightsPage = lazy(() =>
  import('@/pages/InsightsPage').then((m) => ({ default: m.InsightsPage })),
);
const Diagnose = lazy(() => import('@/pages/Diagnose').then((m) => ({ default: m.Diagnose })));
const TransferInitiate = lazy(() =>
  import('@/pages/TransferInitiate').then((m) => ({ default: m.TransferInitiate })),
);
const TransferAccept = lazy(() =>
  import('@/pages/TransferAccept').then((m) => ({ default: m.TransferAccept })),
);

export const router = createBrowserRouter([
  {
    path: '/sign-in',
    element: <SignIn />,
    errorElement: <RouteError />,
  },
  // PUBLIC — no RequireAuth. A buyer/inspector scanning a QR code must reach
  // this page without a Firebase session. Sibling of /sign-in, outside the
  // authenticated app shell.
  {
    path: '/verify/:token',
    element: <VerifyPage />,
    errorElement: <RouteError />,
  },
  // PUBLIC — provenance dossier for a stranger from a QR/shared link.
  // Gated by VITE_PUBLIC_DOSSIER_ENABLED (default off; staging on).
  // The page itself renders NotAvailablePanel when the flag is off —
  // the route always exists so links don't 404 in prod.
  {
    path: '/v/:token',
    element: <PublicDossierPage />,
    errorElement: <RouteError />,
  },
  {
    path: '/',
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/vehicles" replace /> },
      { path: 'vehicles', element: <VehicleList /> },
      { path: 'vehicles/new', element: <VehicleGenesisPage /> },
      { path: 'vehicles/:id', element: <VehicleDetail /> },
      { path: 'vehicles/:id/service', element: <ServiceLog /> },
      { path: 'vehicles/:id/service/:entryId', element: <ServiceEntryDetail /> },
      { path: 'vehicles/:id/diagnose', element: <Diagnose /> },
      { path: 'vehicles/:id/transfer', element: <TransferInitiate /> },
      { path: 'vehicles/:id/projects', element: <ProjectsPage /> },
      { path: 'transfer/accept', element: <TransferAccept /> },
      ...(env.VITE_INSIGHTS_ENABLED ? [{ path: 'insights', element: <InsightsPage /> }] : []),
    ],
  },
  { path: '*', element: <NotFound /> },
]);
